import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { useBottomNavBar } from "../contexts/BottomNavBarContext";
import { BenefitItem, MonthlyBenefit } from "../utils/types";
import { Button } from "../components/Button";
import { UIUtils } from "../utils/uiUtils";
import backArrowIcon from "../assets/icons/back-arrow-icon.svg";

const BenefitDetail: React.FC = () => {
  const { benefitId } = useParams<{ benefitId: string }>();
  const navigate = useNavigate();
  const { benefitsBreakdown, appliedBenefits, toggleBenefitApplied } =
    useUser();
  const { setIsVisible } = useBottomNavBar();
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    return () => setIsVisible(false);
  }, [setIsVisible]);

  // Find the benefit across all categories
  const findBenefit = (): BenefitItem | MonthlyBenefit | null => {
    if (!benefitsBreakdown || !benefitId) return null;

    const allBenefits = [
      ...benefitsBreakdown.month,
      ...benefitsBreakdown.energySupportSocialTariffs,
      ...benefitsBreakdown.discretionary,
      ...benefitsBreakdown.lumpSum,
      ...benefitsBreakdown.homeImprovementsGrants,
      ...benefitsBreakdown.other,
    ];

    return allBenefits.find((benefit) => benefit.id === benefitId) || null;
  };

  const benefit = findBenefit();

  // If benefit not found, show error message
  useEffect(() => {
    if (!benefit && benefitsBreakdown) {
      setShowError(true);
    }
  }, [benefit, benefitsBreakdown]);

  const isMonthlyBenefit = (
    item: BenefitItem | MonthlyBenefit,
  ): item is MonthlyBenefit => {
    return (
      "amount" in item && typeof (item as MonthlyBenefit).amount === "number"
    );
  };

  const handleExternalLink = (url: string, ctaName: string) => {
    UIUtils.trackExternalLinkRedirects(ctaName);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleBackNavigation = () => {
    navigate("/benefits-summary");
  };

  const handleToggleApplied = () => {
    if (benefit) {
      toggleBenefitApplied(benefit.id);
      // Navigate back to benefits summary to show the updated state
      navigate("/benefits-summary");
    }
  };

  // Show error message if benefit not found
  if (showError || (!benefit && benefitsBreakdown)) {
    return (
      <div className="content-container">
        <div className="max-w-3xl mx-auto w-full">
          {/* Back Button */}
          <div className="flex items-center mb-6 pt-4">
            <button
              onClick={handleBackNavigation}
              className="flex items-center"
              aria-label="Go back to benefits summary"
            >
              <img src={backArrowIcon} alt="back" />
            </button>
          </div>

          {/* Error Message */}
          <div className="md:bg-white rounded-30 md:p-6">
            <div className="text-center py-8">
              <p className="text-lg text-gray-600 mb-4">
                Sorry, we are unable to find details on this benefit right now.
                Please try again later.
              </p>
              <Button
                text="Back to Benefits"
                onClick={handleBackNavigation}
                variant="secondary"
                className="mx-auto"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!benefit) {
    return null; // Loading state
  }

  // Check if this benefit is currently marked as applied
  const isApplied = appliedBenefits.includes(benefit.id);

  return (
    <div className="content-container">
      <div className="max-w-3xl mx-auto w-full">
        {/* Back Button */}
        <div className="flex items-center mb-6 pt-4">
          <button
            onClick={handleBackNavigation}
            className="flex items-center"
            aria-label="Go back to benefits summary"
          >
            <img src={backArrowIcon} alt="back" />
          </button>
        </div>

        {/* Benefit Details */}
        {/* Title and Amount */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">
            {benefit.title}
            {isMonthlyBenefit(benefit) && ` - £${benefit.amount.toFixed(2)}`}
          </h1>
        </div>
        <div className="md:bg-white rounded-30 md:p-6">
          {/* Description */}
          <div className="mb-6">
            <p>{benefit.description}</p>
          </div>

          {/* Payment Frequency */}
          {isMonthlyBenefit(benefit) && benefit.paymentFrequency && (
            <div className="mb-6">
              <p className="text-sm">
                <strong>Payment:</strong> {benefit.paymentFrequency}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2">
            {/* First button: CTA from data (if exists) */}
            {benefit.cta && benefit.cta.length > 0 && (
              <div className="w-full">
                <Button
                  text={benefit.cta[0].text}
                  onClick={() =>
                    handleExternalLink(benefit.cta[0].url, benefit.title)
                  }
                  className="w-full"
                  variant="secondary"
                />
              </div>
            )}

            {/* Second button: Toggle Applied Status */}
            <div className="w-full">
              <Button
                text={isApplied ? "Unmark as applied" : "Mark as applied"}
                onClick={handleToggleApplied}
                variant={isApplied ? "secondary" : "light"}
                className={`w-full whitespace-nowrap ${
                  isApplied ? "" : "bg-grey-60 md:bg-grey-20"
                }`}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BenefitDetail;
